import Mustache from 'mustache';

/**
 * @typedef {Object} ChartState
 * @property {boolean} isInitialized
 * @property {HTMLDialogElement|null} modalElement
 * @property {string|null} modalTemplate
 * @property {HTMLElement|null} firstFocusableElement
 * @property {HTMLElement|null} lastFocusableElement
 * @property {HTMLElement|null} previouslyFocusedElement
 */

/**
 * Charts module for handling chart modal interactions.
 */
const charts = {
  /** @type {ChartState} */
  state: {
    isInitialized: false,
    modalElement: null,
    modalTemplate: null,
    firstFocusableElement: null,
    lastFocusableElement: null,
    previouslyFocusedElement: null,
  },

  /**
   * Initializes the modal system and sets up event listeners.
   * @returns {void}
   */
  init() {
    const templateElement = document.getElementById('chart-modal-template');
    if (!templateElement) return;

    const buttonElements = document.querySelectorAll('.chart__modal-button');
    if (buttonElements.length === 0) return;

    if (this.state.isInitialized) return;

    this.state.modalTemplate = templateElement.innerHTML;

    this.state.modalElement = document.createElement('dialog');
    document.body.appendChild(this.state.modalElement);

    this.setupEventListeners();
    this.state.isInitialized = true;
  },

  /**
   * Returns all focusable elements within the modal.
   * @returns {HTMLElement[]}
   * @private
   */
  getFocusableElements() {
    if (!this.state.modalElement) return [];

    const selector = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      'area[href]',
      'iframe',
      'object',
      'embed',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ].join(', ');

    return Array.from(this.state.modalElement.querySelectorAll(selector));
  },

  /**
   * Caches the first and last focusable elements in the modal.
   * @private
   */
  updateFocusableElements() {
    const focusableElements = this.getFocusableElements();
    this.state.firstFocusableElement = focusableElements[0] ?? null;
    this.state.lastFocusableElement = focusableElements[focusableElements.length - 1] ?? null;
  },

  /**
   * Handles Tab key focus trapping within the modal.
   * @param {KeyboardEvent} e - Keyboard event.
   * @private
   */
  handleTabKey(e) {
    if (!this.state.modalElement?.open) return;

    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  },

  /**
   * Sets up click and keyboard event listeners for the modal system.
   * @private
   */
  setupEventListeners() {
    document.addEventListener('click', (e) => {
      const modalButton = e.target.closest('.chart__modal-button');
      if (modalButton) {
        e.preventDefault();
        const modalData = modalButton.closest('[data-chart-modal-data]')?.dataset.chartModalData;
        if (modalData) {
          try {
            this.openModal(JSON.parse(modalData));
          } catch (error) {
            console.error('Charts: Error parsing modal data:', error);
          }
        }
        return;
      }

      if (
        e.target.classList.contains('modal-close') ||
        e.target.closest('.modal-close') ||
        e.target.classList.contains('modal__close-button') ||
        e.target.closest('.modal__close-button') ||
        e.target.classList.contains('modal')
      ) {
        this.closeModal();
        return;
      }

      if (this.state.modalElement?.open) {
        const modalContent = this.state.modalElement.querySelector('.modal');
        if (modalContent && !modalContent.contains(e.target) && e.target === this.state.modalElement) {
          this.closeModal();
        }
      }
    });

    document.addEventListener('keydown', (e) => {
      if (!this.state.modalElement?.open) return;
      if (e.key === 'Escape') {
        this.closeModal();
        return;
      }
      if (e.key === 'Tab') {
        this.handleTabKey(e);
      }
    });
  },

  /**
   * Opens the modal and renders it with the provided data.
   * @param {Object} data - Chart data to render in the modal.
   * @returns {void}
   */
  openModal(data) {
    if (!this.state.isInitialized) {
      this.init();
    }

    if (!this.state.modalElement) return;

    this.state.previouslyFocusedElement = document.activeElement;

    this.renderModal(data);
    this.state.modalElement.showModal();

    this.updateFocusableElements();

    if (this.state.firstFocusableElement) {
      this.state.firstFocusableElement.focus();
    } else {
      this.state.modalElement.focus();
    }

    document.body.style.overflow = 'hidden';
  },

  /**
   * Closes the modal and restores prior state.
   * @returns {void}
   */
  closeModal() {
    if (!this.state.modalElement) return;

    this.state.modalElement.close();
    document.body.style.overflow = '';

    if (this.state.previouslyFocusedElement?.focus) {
      this.state.previouslyFocusedElement.focus();
    }

    this.state.modalElement.innerHTML = '';
    this.state.firstFocusableElement = null;
    this.state.lastFocusableElement = null;
    this.state.previouslyFocusedElement = null;
  },

  /**
   * Renders the Mustache template with the provided data into the modal.
   * @param {Object} data - Chart data to render.
   * @private
   */
  renderModal(data) {
    if (!this.state.modalElement || !this.state.modalTemplate) return;

    try {
      this.state.modalElement.innerHTML = Mustache.render(this.state.modalTemplate, data);
    } catch (error) {
      console.error('Charts: Error rendering modal template:', error);
    }
  },

  /**
   * Removes the modal element from the DOM and resets state.
   * @returns {void}
   */
  destroy() {
    if (this.state.modalElement) {
      this.state.modalElement.remove();
    }
    this.state.isInitialized = false;
    this.state.modalElement = null;
    this.state.modalTemplate = null;
    this.state.firstFocusableElement = null;
    this.state.lastFocusableElement = null;
    this.state.previouslyFocusedElement = null;
  },
};

export default charts;
